import React, { useState } from "react";
import { TextField, Checkbox, Button, Grid, Typography, Container, Box, Paper, Divider, useMediaQuery, useTheme, Alert, Snackbar, CircularProgress, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { Email, Phone, LocationOn, Info } from "@mui/icons-material";
import Notes from "./header";
import FooterManual from "./footerManual";
import { Link, Navigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

// Constants for Color Palette - RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";
const LIGHT_RED_BG = "#FEF2F2";

const redDesignTheme = createTheme({
  palette: {
    primary: {
      main: RED,
    },
    secondary: {
      main: LIGHT_RED_BG,
    },
    background: {
      default: "#FAFAFA",
      paper: "#FFFFFF",
    },
    text: {
      primary: BLACK_TEXT,
      secondary: GRAY_TEXT,
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: RED,
          '&:hover': {
            backgroundColor: DARK_RED_ACCENT,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: RED,
            borderWidth: '2px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: LIGHT_RED_BG,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorSecondary: {
          color: RED,
        },
        root: {
          '&.Mui-checked': {
            color: RED,
          },
        },
      },
    },
  },
});

const customLabels = {
  DZ: "Algeria (+213)",
  AS: "American Samoa (+1-684)",
  AO: "Angola (+244)",
  AG: "Antigua (+1-268)",
  AU: "Australia (+61)",
  BS: "Bahamas (+1-242)",
  BH: "Bahrain (+973)",
  BD: "Bangladesh (+880)",
  BB: "Barbados (+1-246)",
  BE: "Belgium (+32)",
  BJ: "Benin (+229)",
  BM: "Bermuda (+1-441)",
  BW: "Botswana (+267)",
  BN: "Brunei (+673)",
  BF: "Burkina Faso (+226)",
  BI: "Burundi (+257)",
  CM: "Cameroon (+237)",
  CA: "Canada (+1)",
  KY: "Cayman Island (+1-345)",
  CL: "Chile (+56)",
  CO: "Colombia (+57)",
  CI: "Cote D'Ivoire (+225)",
  CY: "Cyprus (+357)",
  CD: "DR Congo (+243)",
  DM: "Dominica (+1-767)",
  EG: "Egypt (+20)",
  ER: "Eritrea (+291)",
  ET: "Ethiopia (+251)",
  FJ: "Fiji (+679)",
  FI: "Finland (+358)",
  FR: "France (+33)",
  GA: "Gabon (+241)",
  GM: "Gambia (+220)",
  DE: "Germany (+49)",
  GH: "Ghana (+233)",
  GR: "Greece (+30)",
  GD: "Grenada (+1-473)",
  GP: "Guadeloupe (+590)",
  GN: "Guinea (+224)",
  GY: "Guyana (+592)",
  HT: "Haiti (+509)",
  HK: "Hongkong (+852)",
  IN: "India (+91)",
  ID: "Indonesia (+62)",
  IE: "Ireland (+353)",
  JM: "Jamaica (+1-876)",
  KE: "Kenya (+254)",
  KI: "Kiribati (+686)",
  KW: "Kuwait (+965)",
  LB: "Lebanon (+961)",
  LR: "Liberia (+231)",
  LY: "Libya (+218)",
  MG: "Madagascar (+261)",
  MW: "Malawi (+265)",
  MY: "Malaysia (+60)",
  MV: "Maldives (+960)",
  MT: "Malta (+356)",
  MH: "Marshall Islands (+692)",
  MQ: "Martinique (+596)",
  MR: "Mauritania (+222)",
  MU: "Mauritius (+230)",
  FM: "Micronesia (+691)",
  MA: "Morocco (+212)",
  MZ: "Mozambique (+258)",
  MM: "Myanmar (+95)",
  NA: "Namibia (+264)",
  NP: "Nepal (+977)",
  NL: "Netherland (+31)",
  BQ: "Netherlands Antilles (+599)",
  NC: "New Caledonia (+687)",
  NZ: "New Zealand (+64)",
  NG: "Nigeria (+234)",
  NO: "Norway (+47)",
  OM: "Oman (+968)",
  PK: "Pakistan (+92)",
  PW: "Palau (+680)",
  PA: "Panama (+507)",
  PG: "Papua New Guinea (+675)",
  QA: "Qatar (+974)",
  RE: "Reunion (+262)",
  RU: "Russia (+7)",
  RW: "Rwanda (+250)",
  KN: "ST. Kitts (+1-869)",
  LC: "ST. Lucia (+1-758)",
  VC: "ST. Vincent (+1-784)",
  WS: "Samoa (+685)",
  SA: "Saudi Arabia (+966)",
  SN: "Senegal (+221)",
  SL: "Sierra Leone (+232)",
  SG: "Singapore (+65)",
  SB: "Solomon Islands (+677)",
  SO: "Somalia (+252)",
  ZA: "South Africa (+27)",
  SS: "South Sudan (+211)",
  LK: "Sri Lanka (+94)",
  SD: "Sudan (+249)",
  SR: "Suriname (+597)",
  SZ: "Swaziland (+268)",
  SE: "Sweden (+46)",
  PF: "Tahiti (+689)",
  TZ: "Tanzania (+255)",
  TH: "Thailand (+66)",
  TG: "Togo (+228)",
  TO: "Tonga (+676)",
  TT: "Trinidad & Tobago (+1-868)",
  TR: "Turkey (+90)",
  TV: "Tuvalu (+688)",
  AE: "U.A.E (+971)",
  UG: "Uganda (+256)",
  GB: "United Kingdom (+44)",
  US: "United States (+1)",
  VU: "Vanuatu (+678)",
  VE: "Venezuela (+58)",
  YE: "Yemen (+967)",
  ZM: "Zambia (+260)",
  ZW: "Zimbabwe (+263)",
  JP: "Japan (+81)"
};

const ContactForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    countryCode: "",
    mobileNo: "",
    yourMessage: "",
  });

  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle consent checkbox
  const handleConsentChange = (e) => {
    setConsent(e.target.checked);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Validate form
  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter your first name",
        severity: "warning",
      });
      return false;
    }
    if (!formData.lastName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter your last name",
        severity: "warning",
      });
      return false;
    }
    if (!formData.emailId.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter your email",
        severity: "warning",
      });
      return false;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.emailId)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address",
        severity: "warning",
      });
      return false;
    }
    if (!formData.yourMessage.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter your message",
        severity: "warning",
      });
      return false;
    }
    if (!consent) {
      setSnackbar({
        open: true,
        message: "Please agree to the consent",
        severity: "warning",
      });
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Extract country code from the selected country
      const selectedCountryCode = customLabels[formData.countryCode].match(/\+[\d-]+/)[0];
      const fullPhoneNumber = formData.mobileNo ? `${selectedCountryCode}${formData.mobileNo}` : "";

      const response = await fetch(
        "https://car.amar.co.jp/api/authorization/contact-us/sent-amarcar-inquiry",
        {
          method: "POST",
          headers: {
            "accept": "application/json",
            "Accept-Language": "en",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            emailId: formData.emailId,
            mobileNo: fullPhoneNumber,
            yourMessage: formData.yourMessage,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Success
        setSnackbar({
          open: true,
          message: "Your inquiry has been sent successfully! We'll get back to you soon.",
          severity: "success",
        });

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          emailId: "",
          countryCode: "JP",
          mobileNo: "",
          yourMessage: "",
        });
        setConsent(false);
      } else {
        // Error from API
        setSnackbar({
          open: true,
          message: data.message || "Failed to send inquiry. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error sending inquiry:", error);
      setSnackbar({
        open: true,
        message: "An error occurred. Please check your internet connection and try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={redDesignTheme}>
      <Box sx={{ marginBottom: "0%" }}><Notes /></Box>
      
      {/* Hero Banner Section */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
          backgroundColor: "black",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src="/image/AllSeeModelsBackgroungCar.png"
          alt="Car Background"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.35,
          }}
        />

        {/* Page Title */}
        <Typography
          sx={{
            position: "relative",
            color: "white",
            fontWeight: "900",
            textTransform: "uppercase",
            letterSpacing: isMobile ? "1px" : isTablet ? "2px" : "4px",
            fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
            zIndex: 2,
            textShadow: "0 0 10px rgba(0,0,0,0.8)",
            textAlign: "center",
            lineHeight: 1.1,
            px: "20px",
          }}
        >
          Contact Us
        </Typography>
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          width: isMobile ? "95%" : isTablet ? "92%" : "85%",
          backgroundColor: "#FFFFFF",
          margin: "0 auto",
          marginTop: isMobile ? "-50px" : isTablet ? "-60px" : "-80px",
          borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
          padding: isMobile ? "20px 16px" : isTablet ? "32px 24px" : "40px 30px",
          boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
          zIndex: 10,
          position: "relative",
        }}
      >
        {/* Contact Form Section */}
        <Typography
          variant="h4"
          fontWeight="bold"
          color="primary.main"
          gutterBottom
          align="center"
          mb={{ xs: 2, md: 4 }}
          sx={{
            fontSize: { xs: "1.5rem", sm: "1.875rem", md: "2.125rem" },
          }}
        >
          Send Us a Message
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 4 }}>
            {/* Left Side Fields */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={loading}
                    sx={{ 
                      "& .MuiOutlinedInput-root": {
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Surname"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E-mail"
                    name="emailId"
                    type="email"
                    value={formData.emailId}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl 
                      variant="outlined" 
                      size="small"
                      sx={{ minWidth: { xs: 120, sm: 140 } }}
                      disabled={loading}
                    >
                      <InputLabel id="country-code-label">Country</InputLabel>
                      <Select
                        labelId="country-code-label"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        label="Country"
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                        }}
                      >
                        {Object.entries(customLabels).map(([code, label]) => (
                          <MenuItem key={code} value={code}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Phone Number"
                      name="mobileNo"
                      type="tel"
                      value={formData.mobileNo}
                      onChange={handleChange}
                      fullWidth
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled={loading}
                      placeholder="Enter phone number"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: { xs: "0.875rem", md: "1rem" },
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Side Message Field */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Your Message"
                name="yourMessage"
                value={formData.yourMessage}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
                required
                variant="outlined"
                color="primary"
                size="small"
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    minHeight: { xs: "80px", md: "105px" },
                    alignItems: "flex-start",
                  },
                  "& textarea": {
                    padding: "14px",
                  },
                }}
              />
            </Grid>

            {/* Consent Checkbox and Button */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2, md: 3 },
                }}
              >
                {/* Consent */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  maxWidth: 800,
                  gap: { xs: 0.5, md: 1 },
                }}>
                  <Checkbox 
                    required 
                    color="primary" 
                    sx={{ mt: -1 }}
                    checked={consent}
                    onChange={handleConsentChange}
                    disabled={loading}
                  />
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                      lineHeight: 1.4,
                    }}
                  >
                    I accept the{" "}
                    <span
                      onClick={() => navigate("/termsandCondition")}
                      style={{
                        color: "#1976d2",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      Terms & Conditions
                    </span>{" "}
                    and agree to receive marketing communications and personalized services.
                  </Typography>
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  sx={{
                    width: { xs: "100%", sm: "300px" },
                    height: { xs: "45px", md: "55px" },
                    fontWeight: 'bold',
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    boxShadow: `0px 4px 15px rgba(220, 38, 38, 0.4)`,
                    transition: 'all 0.3s ease',
                  }}
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Send Your Inquiry"
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Divider */}
        <Divider sx={{
          my: { xs: 3, sm: 4, md: 6 },
          borderColor: 'secondary.main',
          borderWidth: '2px',
        }} />

        {/* Contact Information Section */}
        <Box mt={{ xs: 2.5, sm: 3, md: 5 }}>
          <Typography
            fontWeight="600"
            variant="h5"
            align="center"
            mb={{ xs: 2, sm: 3, md: 4 }}
            color="primary.main"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
            }}
          >
            Contact Information
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {[
              { icon: LocationOn, title: "Head Office Address", content: "Amar Enterprises Co Ltd, 1076-1 Oooaza Komizou Iwatsuki Saitama Shi Saitama-Ken -339-0003 Japan" },
              { icon: Email, title: "Email", content: "sales@amar.co.jp" },
              { icon: Phone, title: "Phone", content: "+81-48-793-3911" },
              { icon: Info, title: "License Details", content: "Secondhand Goods Car Dealer License No-Saitama Prefectural Public Safety Commission No-431260012433 Qualification Registration Number T7-0300-0202-3122" },
            ].map((item, index) => (
              <Grid
                item xs={12} sm={6} md={3}
                key={index}
                textAlign="center"
                sx={{
                  borderRight: {
                    xs: "none",
                    md: index < 3 ? "1px solid #FFCDD2" : "none",
                  },
                  borderBottom: {
                    xs: index < 3 ? "1px solid #FFCDD2" : "none",
                    md: "none",
                  },
                  p: { xs: 1.5, sm: 2 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    borderRadius: '50%',
                    display: 'inline-flex',
                    p: { xs: 1, md: 1.5 },
                    mb: 1,
                  }}
                >
                  <item.icon
                    color="primary"
                    fontSize="large"
                    sx={{
                      fontSize: { xs: "1.75rem", md: "2.5rem" },
                    }}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary.main"
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                    mb: 1,
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.875rem" },
                    lineHeight: 1.4,
                  }}
                >
                  {item.content}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Space Filler for Footer Alignment */}
      <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />

      <FooterManual />
    </ThemeProvider>
  );
};

export default ContactForm;